import React from 'react';
import moment from 'moment';
import { browserHistory } from 'react-router';
import { IconButton } from '@zola/zola-ui';
import { trackOptimizelyEvent } from '@zola-helpers/client';

import StoriesNavigationButton from './StoriesNavigationButton';
import StoriesProgressBar from './StoriesProgressBar';
import StoriesSlides from './StoriesSlides';

import './Stories.less';

const stories = [
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide1-optimized.mp4',
    duration: 8000,
    type: 'video',
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide2-optimized.mp4',
    type: 'video',
    duration: 6000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide3-optimized.mp4',
    type: 'video',
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide4-optimized.mp4',
    type: 'video',
    duration: 8000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide5-optimized.mp4',
    type: 'video',
    duration: 5000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide6-optimized.mp4',
    type: 'video',
    duration: 9000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide7-optimized.mp4',
    type: 'video',
    duration: 5000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide8-optimized.mp4',
    type: 'video',
    duration: 7000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide9-optimized.mp4',
    type: 'video',
    duration: 9000,
  },
  {
    url: 'https://zola-web-assets.s3.amazonaws.com/web-wedding/zola-stories/zolastories-slide10-optimized.mp4',
    type: 'video',
    duration: 5000,
  },
];

export default class Stories extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentIndex: 0,
      isActive: true,
      isPaused: false,
      assetIsReady: false,
    };

    this.timer = null;
    this.time = {
      currentInterval: 10000,
      started: null,
      elapsed: null,
    };

    this.isLongTouchEvent = false;

    this.progressBarRefs = [];
    this.slideRefs = [];
  }

  componentDidMount() {
    document.getElementsByTagName('body')[0].classList.add('zostories__open');
    this.isAssetReady();
  }

  componentDidUpdate(prevProps, prevState) {
    const { currentIndex } = this.state;
    if (currentIndex !== prevState.currentIndex) {
      this.isAssetReady();
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  setTimer = (interval = 10000) => {
    this.time = {
      currentInterval: interval,
      started: moment(),
      elapsed: null
    };
    this.timer = setInterval(this.handleGoToNextSlide, interval);
  }

  pauseTimer = () => {
    clearInterval(this.timer);
    const now = moment();
    this.time.elapsed = now.diff(this.time.started);
  }

  resumeTimer = () => {
    const { currentInterval, elapsed } = this.time;
    this.setTimer(currentInterval - elapsed);
  }

  isAssetReady = () => {
    const { currentIndex } = this.state;
    const currentAsset = this.slideRefs[currentIndex];

    const TIMEOUT_THRESHOLD = 3000;
    const READY_STATE = 4; // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
    const assetIsReady = (currentAsset.tagName === 'VIDEO' && currentAsset.readyState === READY_STATE) || currentAsset.tagName !== 'VIDEO';

    if (assetIsReady) {
      this.startStory();
    } else {
      currentAsset.addEventListener('canplaythrough', this.startStory); // equivalent of readyState === 4
      this.setState({ assetIsReady: false });
      this.assetTimeout = setTimeout(this.trackTimeout, TIMEOUT_THRESHOLD);
    }
  }

  startStory = () => {
    const { currentIndex } = this.state;
    const currentAsset = this.slideRefs[currentIndex];

    clearTimeout(this.assetTimeout);
    if (currentAsset.tagName === 'VIDEO') currentAsset.removeEventListener('canplaythrough', this.startStory);
    this.setState({ assetIsReady: true });
    this.setTimer(stories[currentIndex].duration);
    this.restartVideo(currentIndex);
  }

  trackTimeout = () => trackOptimizelyEvent('Zola Stories - Asset Timeout');

  setProgressBarRefs = (ref, i) => {
    this.progressBarRefs[i] = ref;
  }

  setSlideRefs = (ref, i) => {
    this.slideRefs[i] = ref;
  }

  restartAnimation = () => {
    const { currentIndex } = this.state;
    const el = this.progressBarRefs[currentIndex];
    el.style.animation = 'none';
    // offsetHeight triggers reflow
    el.offsetHeight; // eslint-disable-line no-unused-expressions
    el.style.animation = null; // restartAnimation
  }

  // reset video and start playing again
  restartVideo = (assetIndex) => {
    const asset = this.slideRefs[assetIndex];

    if (asset.tagName === 'VIDEO') {
      asset.pause();
      asset.currentTime = 0;
      asset.play();
    }
  }

  // reset video and keep at beginning
  resetVideo = (assetIndex) => {
    const asset = this.slideRefs[assetIndex];

    if (asset && asset.tagName === 'VIDEO') {
      asset.pause();
      asset.currentTime = 0;
    }
  }

  pauseVideo = () => {
    const { currentIndex } = this.state;
    const currentAsset = this.slideRefs[currentIndex];
    if (currentAsset.tagName === 'VIDEO') {
      currentAsset.pause();
    }
  }

  playVideo = () => {
    const { currentIndex } = this.state;
    const currentAsset = this.slideRefs[currentIndex];
    if (currentAsset.tagName === 'VIDEO') {
      currentAsset.play();
    }
  }

  handleGoToNextSlide = () => {
    const { currentIndex } = this.state;
    const nextIndex = currentIndex + 1;
    const endOfStories = currentIndex === stories.length - 1;

    clearInterval(this.timer);

    if (endOfStories) {
      return this.handleStoriesClose();
    }

    this.resetVideo(nextIndex);

    return this.setState({ currentIndex: nextIndex, assetIsReady: false }, () => this.resetVideo(currentIndex));
  }

  handleGoToPreviousSlide = () => {
    const { currentIndex } = this.state;
    const prevIndex = currentIndex - 1;

    this.resetVideo(prevIndex);
    clearInterval(this.timer);

    // beginning slide, restart timer/animation
    if (currentIndex === 0) {
      this.restartAnimation();
      this.restartVideo(currentIndex);
      return this.setTimer(stories[currentIndex].duration);
    }

    return this.setState({ currentIndex: prevIndex, assetIsReady: false }, () => this.resetVideo(currentIndex));
  }

  handleStoriesClose = () => {
    clearInterval(this.timer);
    clearTimeout(this.assetTimeout);
    this.setState(prevState => ({ isActive: !prevState.isActive }));
    document.getElementsByTagName('body')[0].classList.remove('zostories__open');
    browserHistory.replace({ pathname: window.location.pathname, state: { showZolaStories: false } });
  }

  handleTouchStart = () => {
    this.longTouchEvent = setTimeout(() => {
      this.isLongTouchEvent = true;
      this.pauseTimer();
      this.setState({ isPaused: true }, this.pauseVideo);
    }, 500);
  }

  handleTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.isLongTouchEvent) {
      this.isLongTouchEvent = false;
      this.resumeTimer();
      return this.setState({ isPaused: false }, this.playVideo);
    }

    clearTimeout(this.longTouchEvent);
    const { action } = e.target.dataset;
    if (action === 'prev') {
      return this.handleGoToPreviousSlide();
    }
    return this.handleGoToNextSlide();
  }

  render() {
    const { isActive, isPaused, currentIndex, assetIsReady } = this.state;

    if (!isActive) return null;

    return (
      <div className="zostories">
        <IconButton iconClassName="zola-ui-icon-close" className="zostories__close" btnStyle="tertiary" onClick={this.handleStoriesClose} />
        <StoriesProgressBar stories={stories} isPaused={isPaused} currentIndex={currentIndex} setProgressBarRefs={this.setProgressBarRefs} assetIsReady={assetIsReady} />
        <StoriesNavigationButton
          direction="prev"
          label="Previous"
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
        />
        <StoriesSlides stories={stories} currentIndex={currentIndex} setSlideRefs={this.setSlideRefs} assetIsReady={assetIsReady} />
        <StoriesNavigationButton
          direction="next"
          label="Next"
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/onboard/Stories/Stories.jsx